import { useCallback, useMemo } from 'react';

import { useListCampaignFundraisers } from '@givebacks/api/store';
import { useSearchParams } from 'react-router-dom';
import { CampaignCardList } from 'store/Components';
import { ActionIcon, Center, Loader, NoResults, Stack, TextInput } from 'ui';
import { useDebouncedValue } from 'ui/Hooks';
import { IconSearch, IconX } from 'ui/Icons';

import { useCausePageContext } from './CauseContext';

const Campaigns = () => {
  const { cause } = useCausePageContext();

  const [searchParams, setSearchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') ?? '';
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery, 500);

  const setSearchQuery = useCallback(
    (value: string) => {
      const searchParamsCopy = new URLSearchParams(searchParams);
      if (value) {
        searchParamsCopy.set('search', value);
      } else {
        searchParamsCopy.delete('search');
      }
      setSearchParams(searchParamsCopy);
    },
    [setSearchParams, searchParams]
  );

  const { data: campaigns, isFetching } = useListCampaignFundraisers({
    causeId: cause?.uuid,
    queryOptions: {
      searches: {
        name: {
          value: debouncedSearchQuery,
        },
        live: {
          value: true,
        },
        type: {
          value: 'StoreItems::Campaign,StoreItems::Athon,StoreItems::RallyUp',
          by: 'in',
        },
      },
      join: 'AND',
    },
  });

  const hasResults = useMemo(() => campaigns && campaigns?.store_items?.length > 0, [campaigns]);

  return (
    <Stack gap="md" mt="sm">
      <TextInput
        placeholder="Search all fundraisers"
        value={searchQuery}
        onChange={(event) => setSearchQuery(event.currentTarget.value)}
        leftSection={<IconSearch size={16} />}
        rightSection={
          searchQuery ? (
            <ActionIcon
              size="sm"
              variant="transparent"
              c="gray"
              aria-label="Clear search"
              onClick={() => setSearchQuery('')}
            >
              <IconX size={16} />
            </ActionIcon>
          ) : null
        }
        mb="sm"
      />
      {isFetching && (
        <Center>
          <Loader />
        </Center>
      )}
      {!isFetching && hasResults ? (
        <CampaignCardList
          data={campaigns?.store_items ?? []}
          causeId={cause?.uuid}
          isStacked
          isDumbCard
          navigateToCampaign={(navigateToCampaign, campaignId) => {
            navigateToCampaign(campaignId);
          }}
          campaignCardProps={{ isColumn: false, hideAmountRaised: true }}
        />
      ) : (
        <NoResults isLoading={isFetching} />
      )}
    </Stack>
  );
};

export { Campaigns };
